import { Flex, Heading, VStack, motion, useTheme } from '@hover/blueprint';
import { iconPlus } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { useProTranslate } from 'src/modules/pro/hooks';
import { Button } from 'src/shared/components';
import type { ButtonPressed } from 'src/shared/telemetry';

const StickyCTA = ({
  onTryItClick,
  ctaTrackingData,
  isVisible,
}: {
  onTryItClick: () => void;
  ctaTrackingData: Partial<ButtonPressed>;
  isVisible: boolean;
}) => {
  const t = useProTranslate();

  const { job_id: _, ...relevantTrackingData } = ctaTrackingData;
  const { shadows } = useTheme();

  const variants = {
    visible: {
      y: '0%',
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
      boxShadow: shadows.distance400,
    },
    hidden: {
      y: '100%',
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
      boxShadow: shadows.invisibleNeutral,
    },
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          animate="visible"
          bg="neutral.0"
          bottom={0}
          exit="hidden"
          initial="hidden"
          left={0}
          position="fixed"
          right={0}
          variants={variants}
        >
          <Flex
            alignItems={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start',
            }}
            display={{ mobile: 'flex', tablet: 'flex', desktop: 'none' }}
            flexDirection={{ base: 'column', tablet: 'row', desktop: 'column' }}
            height="100%"
            justifyContent="center"
            margin="0 auto"
            padding={500}
          >
            <VStack
              alignItems="flex-start"
              display={{ tablet: 'flex', mobile: 'none' }}
              flexGrow={1}
              pb={{ base: 500, tablet: 0, desktop: 200 }}
            >
              <Heading
                fontWeight="bold"
                mb={{ base: 200, tablet: 100, desktop: 200 }}
                size={{ tablet: 500, desktop: 300, width500: 500 }}
              >
                {t('tryItCard.header')}
              </Heading>
            </VStack>
            <Button
              iconBefore={iconPlus}
              marginLeft={{ base: 0, tablet: 700, desktop: 0 }}
              onClick={onTryItClick}
              optionalTrackingProperties={relevantTrackingData}
              size={{ base: 'large', desktop: 'medium', width500: 'large' }}
              width={{ base: '100%', tablet: 'fit-content', desktop: '100%' }}
            >
              {t('tryItCard.buttonText')}
            </Button>
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { StickyCTA };
